import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import * as pas from '../pas';
import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useLoginRequest } from './useLoginRequest';
import { WAITING_MESSAGE_LOGIN_TYPE } from './constants';
import { useValidateSession } from './useValidateSession';
import { useAfterLoginAction } from './useAfterLoginAction';
import { useBeforeLoginAction } from './useBeforeLoginAction';
import { useFailedLoginAction } from './useFailedLoginAction';
import { useSuccessLoginAction } from './useSuccessLoginAction';
import { useGetWaitingMessages } from './useGetWaitingMessages';
import { useLoginBySessionToken } from './useLoginBySessionToken';
import { useRedirectToAfterLoginURL } from './useRedirectToAfterLoginURL';

export const useLoginBySSO = () => {
    const dispatch = useDispatch();
    const beforeLoginAction = useBeforeLoginAction();
    const loginBySessionToken = useLoginBySessionToken();
    const loginByCookie = useLoginByCookie();
    const afterLoginAction = useAfterLoginAction();
    const successLoginAction = useSuccessLoginAction();
    const failedLoginAction = useFailedLoginAction();
    const redirectToAfterLoginURL = useRedirectToAfterLoginURL();
    const validateSession = useValidateSession();
    const getWaitingMessages = useGetWaitingMessages();
    const { realMode, languageCode } = useLoginRequest() || {};

    return useCallback(async () => {
        dispatch(actions.setIsLoginInProcess(true));
        dispatch(actions.setSsoAttempted());
        beforeLoginAction();

        try {
            const response = await loginByCookie();
            const { sessionValidationData } = response?.data || {};

            if (sessionValidationData) {
                const userName = helpers.retrieveRememberMeItem();
                const credentials = { userName, password: '', realMode, languageCode, rememberMeLogin: true };

                dispatch(actions.setIsLoginInProcess(false));
                dispatch(actions.setAutoLoginCompleted());

                validateSession(response, credentials, { isSSO: true });
            }
        } catch (error) {
            failedLoginAction({
                data: 'PAS SSO login is failed. Cookie does not exists.',
                ssoLogin: true
            });
            dispatch(actions.setIsLoginInProcess(false));
            dispatch(actions.setAutoLoginCompleted());

            return;
        }

        try {
            const tempTokenResponse = await pas.getTempToken();
            const userName = tempTokenResponse?.data?.username;
            const sessionToken = tempTokenResponse?.data?.sessionToken?.sessionToken;

            await loginBySessionToken(userName, sessionToken);
            await afterLoginAction();
            await successLoginAction({ userName, authType: 'token' });

            await getWaitingMessages({ excludeWaitingMessages: WAITING_MESSAGE_LOGIN_TYPE });

            redirectToAfterLoginURL();
        } catch (error) {
            helpers.removeRememberMeItem();
            failedLoginAction({
                data: 'PAS SSO login is failed. Cookie does not exists.',
                ssoLogin: true
            });
        } finally {
            dispatch(actions.setIsLoginInProcess(false));
            dispatch(actions.setAutoLoginCompleted());
        }
    }, [
        realMode,
        languageCode,
        dispatch,
        beforeLoginAction,
        loginByCookie,
        loginBySessionToken,
        getWaitingMessages,
        afterLoginAction,
        successLoginAction,
        failedLoginAction,
        redirectToAfterLoginURL,
        validateSession
    ]);
};

export const useLoginByCookie = () => {
    const loginByRememberMe = useLoginByRememberMe();

    return useCallback(async () => {
        const response = await pas.getLoggedInPlayer();

        if (response?.data?.cookieExists === '1') {
            return;
        }

        return loginByRememberMe();
    }, [loginByRememberMe]);
};

export const useLoginByRememberMe = () => {
    const { realMode, languageCode } = useLoginRequest() || {};

    return useCallback(() => {
        const userName = helpers.retrieveRememberMeItem();
        const credentials = { userName, password: '', realMode, languageCode, rememberMeLogin: true };

        if (!userName) {
            return Promise.reject();
        }

        return pas.login(credentials);
    }, [realMode, languageCode]);
};
